/* eslint-disable react/jsx-props-no-spreading */

/* eslint-disable react/jsx-indent */
import classNames from 'classnames';
import omit from 'lodash/omit';
import PropTypes from 'prop-types';
import React, { useMemo, useRef, useEffect, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';

import useDebounce from '../../hooks/useDebounce';
import useQuery from '../../hooks/useQuery';
import * as AppPropTypes from '../../lib/PropTypes';

import { useSearch } from '../../contexts/SearchContext';
import CalendarHeader from '../headers/Calendar';
import IndexHeader from '../headers/IndexHeader';
import ArticlesList from '../lists/ArticlesList';
import CalendarList from '../lists/CalendarList';
import IndexList from '../lists/IndexList';
import SearchIndexList from '../lists/SearchIndexList';
import NoEdition from '../partials/NoEdition';

import styles from '../../../styles/pages/index.module.scss';

const propTypes = {
    page: AppPropTypes.page.isRequired,
    queryString: PropTypes.string,
    className: PropTypes.string,
};

const defaultProps = {
    queryString: null,
    className: null,
};

const listComponentByHandle = {
    articles: ArticlesList,
    calendar: CalendarList,
};

const IndexPage = ({ page: indexPage, queryString, className }) => {
    const {
        title,
        description,
        section = null,
        blocks,
        handle,
        parent = null,
        itemType: pageItemType,
        repository,
        filters,
        url,
        pages = null,
        query: mainQuery = null,
        edition = null,
    } = indexPage || {};
    const { upcoming = false } = edition || {};
    const isUpcoming = upcoming && handle === 'films';

    const [itemType, setItemType] = useState(pageItemType);
    useEffect(() => {
        setItemType(pageItemType);
    }, [pageItemType]);

    const { query = null, setQuery, resetQuery } = useQuery(url, queryString);

    const { handle: parentHandle = null } = parent || {};
    const groupHandle = parentHandle || handle;
    const showSearch = handle === 'search' && parent === null;
    const inSearchIndex = handle === 'search';
    const isCalendar = handle === 'calendar';

    const initialQuery = useMemo(
        () => ({
            ...mainQuery,
            ...query,
            ...(inSearchIndex ? { index: 'search' } : null),
        }),
        [showSearch, parentHandle],
    );

    const finalQuery = useMemo(
        () => ({
            ...mainQuery,
            ...query,
            ...(inSearchIndex ? { index: 'search' } : null),
        }),
        [query, mainQuery, showSearch, parentHandle],
    );

    const pageRef = useRef(indexPage);
    const queryRef = useRef(finalQuery);
    const queryHasChanged = queryRef.current !== finalQuery;
    const pageHasChanged = pageRef.current !== indexPage;
    const initialPages = useMemo(
        () => (!queryHasChanged || pageHasChanged ? pages : null),
        [pages, queryHasChanged],
    );
    const isDark = handle !== 'articles' && parentHandle !== 'articles';

    useEffect(() => {
        queryRef.current = finalQuery;
    }, [finalQuery]);

    useEffect(() => {
        pageRef.current = indexPage;
    }, [indexPage]);

    // Calendar filter state
    const calendarFilter = (filters || []).find(({ type = null }) => type === 'calendar');
    const { dates = null } = calendarFilter || {};
    const [activeDates, setActiveDates] = useState([]);

    const date = (activeDates || []).find((item, index) => index === 0);
    // TODO: get today's date. If today's month contains days, set defaultDate to first item in today's date
    const defaultDate = (dates || []).find((item, index) => index === 0);
    const [activeMonth, setActiveMonth] = useState(date || defaultDate);

    // Manage search query
    const { value: searchValue, setValue: setSearchValue } = useSearch();
    const onSearchChange = useCallback(
        (newValue) => {
            setQuery({ ...query, search: newValue || null });
        },
        [query, setQuery],
    );
    useDebounce(inSearchIndex ? onSearchChange : null, searchValue, 400);

    // Back to parent page
    const history = useHistory();

    const onQueryChange = useCallback(
        (newValue) => {
            setQuery(newValue !== null ? omit(newValue, ['scope']) : null);
        },
        [setQuery],
    );

    const onQueryReset = useCallback(
        (e) => {
            resetQuery(e);
            setSearchValue(null);
            if (parent !== null) {
                history.push(parent.url);
            }
        },
        [resetQuery, parent, history],
    );

    const onItemTypeChange = useCallback(
        (newItemType) => {
            if (newItemType !== null) {
                setItemType(newItemType);
            }
        },
        [setItemType],
    );

    // Section specific header
    const { url: sectionUrl = null, lengthsLabel = null } = section || {};
    const hasSection = sectionUrl !== null;
    const headerButtonProps = hasSection
        ? {
              buttonUrl: sectionUrl,
              buttonLabel: (
                  <FormattedMessage defaultMessage="View section" description="Button label" />
              ),
          }
        : null;

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                    [styles.isDark]: isDark,
                },
            ])}
        >
            {showSearch ? (
                <SearchIndexList
                    url={url}
                    header={
                        <IndexHeader
                            title={title}
                            isDark={isDark}
                            blocks={blocks}
                            withSearchBar
                            searchClassName={styles.search}
                            setSearchValue={setSearchValue}
                            searchValue={searchValue}
                        />
                    }
                    filters={filters}
                    initialPages={initialPages}
                    query={finalQuery}
                    onQueryChange={setQuery}
                    onQueryReset={onQueryReset}
                    isDark={isDark}
                />
            ) : (
                <IndexList
                    title={title}
                    url={url}
                    section={section}
                    emptyElement={isUpcoming ? <NoEdition isDark /> : null}
                    header={
                        isCalendar ? (
                            <CalendarHeader
                                title={title}
                                description={description}
                                isDark={isDark}
                                dates={dates}
                                activeDates={activeDates}
                                setActiveDates={setActiveDates}
                                activeMonth={activeMonth}
                                setActiveMonth={setActiveMonth}
                                blocks={blocks}
                                searchClassName={styles.search}
                                setSearchValue={setSearchValue}
                                searchValue={searchValue}
                            />
                        ) : (
                            <IndexHeader
                                title={title}
                                description={lengthsLabel}
                                isDark={isDark}
                                blocks={blocks}
                                withSearchBar={inSearchIndex}
                                searchClassName={styles.search}
                                setSearchValue={setSearchValue}
                                searchValue={searchValue}
                                {...headerButtonProps}
                            />
                        )
                    }
                    repository={repository}
                    cardType={itemType}
                    cardProps={{
                        isModal: itemType === 'film' || itemType === 'section',
                        isCompact: handle === 'articles',
                        isDark,
                        withYear: inSearchIndex,
                        shuffle: itemType === 'section',
                    }}
                    listComponent={listComponentByHandle[groupHandle] || null}
                    listProps={{
                        withColumns: groupHandle === 'articles',
                        cardProps: {
                            isStacked: groupHandle === 'articles',
                        },
                        isDark: groupHandle === 'articles' || groupHandle === 'calendar',
                    }}
                    filters={filters}
                    initialPages={handle !== 'search' ? initialPages : null}
                    query={finalQuery}
                    initialQuery={initialQuery}
                    onQueryChange={onQueryChange}
                    onQueryReset={onQueryReset}
                    itemType={itemType}
                    onItemTypeChange={onItemTypeChange}
                    isGrid={itemType === 'film'}
                    gridColumns={groupHandle === 'articles' ? 3 : 0}
                    isDark={isDark}
                    itemClassName={inSearchIndex ? styles.searchItem : null}
                />
            )}
        </div>
    );
};

IndexPage.propTypes = propTypes;
IndexPage.defaultProps = defaultProps;

export default IndexPage;
